<template>
  <div class="layout">
    <aside class="layout-sidebar" :class="{ 'layout-sidebar--open': mobileMenuOpen }">
      <div class="brand-card">
        <div class="brand-mark">P</div>
        <div>
          <div class="brand-title">爬宠后台</div>
          <div class="brand-desc">运营管理与数据工具</div>
        </div>
      </div>

      <nav class="menu-list" aria-label="后台菜单">
        <div v-for="group in visibleMenuGroups" :key="group.key" class="menu-group">
          <button
            type="button"
            class="menu-group__head"
            :class="{ 'menu-group__head--active': isGroupActive(group) }"
            :aria-expanded="String(isGroupOpen(group.key))"
            @click="toggleMenuGroup(group.key)"
          >
            <span class="menu-group__icon">{{ group.icon }}</span>
            <span class="menu-group__title">{{ group.title }}</span>
            <span
              class="menu-group__arrow"
              :class="{ 'menu-group__arrow--open': isGroupOpen(group.key) }"
            >⌄</span>
          </button>

          <div v-show="isGroupOpen(group.key)" class="submenu-list">
            <router-link
              v-for="item in group.children"
              :key="item.path"
              :to="item.path"
              class="menu-item"
              exact-active-class="menu-item--active"
            >
              <span class="menu-item__icon">{{ item.icon }}</span>
              <span>{{ item.title }}</span>
            </router-link>
          </div>
        </div>
      </nav>
    </aside>
    <button v-if="mobileMenuOpen" class="mobile-menu-overlay" type="button" aria-label="关闭菜单" @click="mobileMenuOpen = false"></button>

    <div class="layout-main">
      <header class="layout-header">
        <button class="mobile-menu-button" type="button" aria-label="打开菜单" @click="mobileMenuOpen = true">
          <span></span><span></span><span></span>
        </button>
        <div class="layout-header__copy">
          <div class="layout-title">{{ currentTitle }}</div>
          <div class="layout-subtitle">{{ currentSubtitle }}</div>
        </div>
        <div class="layout-user">
          <span class="layout-user__badge">{{ currentUser.displayName || currentUser.username || "内部账号" }}</span>
          <button class="ghost-button" @click="logout">退出登录</button>
        </div>
      </header>

      <main class="layout-content">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script>
import { fetchCurrentAdmin, logoutAdmin } from "../api/adminAuth"
import { clearAdminSession, getAdminUser, updateAdminUser } from "../utils/adminSession"

export default {
  name: "DashboardLayout",
  created() {
    this.refreshAdminPermissions()
  },
  data() {
    return {
      currentUserData: getAdminUser() || {},
      mobileMenuOpen: false,
      openMenuKeys: {
        overview: true,
        profile: true,
        operation: true,
        activity: true,
        intelligence: true,
        office: true,
        system: true
      },
      menuGroups: [
        {
          key: "overview",
          title: "首页总览",
          icon: "总",
          children: [
            { path: "/dashboard", title: "工作台", icon: "台", permission: "dashboard:view" },
            { path: "/banner-settings", title: "轮播图设置", icon: "轮", permission: "operation:banner" }
          ]
        },
        {
          key: "profile",
          title: "用户与宠物",
          icon: "档",
          children: [
            { path: "/users", title: "用户管理", icon: "人", permission: "users:view" },
            { path: "/pets", title: "宠物资料", icon: "宠", permission: "pets:view" },
            { path: "/pet-types", title: "宠物类型", icon: "类", permission: "catalog:view" }
          ]
        },
        {
          key: "operation",
          title: "运营配置",
          icon: "营",
          children: [
            { path: "/feeding-plans", title: "喂食模板", icon: "喂", permission: "operation:feeding" },
            { path: "/square-topics", title: "广场话题", icon: "话", permission: "operation:square_topic" },
            { path: "/square-manage", title: "广场管理", icon: "广", permission: "square:manage" },
            { path: "/content-audits", title: "内容审核管理", icon: "审", permission: "content:audit:view" },
            { path: "/product-reviews", title: "商品审核", icon: "审", permission: "operation:shop_review" },
            { path: "/merchant-accounts", title: "商户账号", icon: "商", permission: "operation:merchant_account:edit" },
            { path: "/app-themes", title: "主题换肤", icon: "肤", permission: "operation:theme" },
            { path: "/about", title: "关于我们", icon: "我", permission: "operation:theme" },
            { path: "/ai-images", title: "生图管理", icon: "图", permission: "operation:ai_image" }
          ]
        },
        {
          key: "activity",
          title: "活动",
          icon: "奖",
          children: [
            { path: "/activity-lottery", title: "抽奖活动", icon: "奖", permission: "activity:view" },
            { path: "/simulation", title: "模拟互动", icon: "测", permission: "simulation:manage" },
            { path: "/activity-config", title: "活动配置", icon: "配", permission: "activity:review" },
            { path: "/invite-codes", title: "邀请码管理", icon: "码", permission: "activity:view" }
          ]
        },
        {
          key: "intelligence",
          title: "智能工具",
          icon: "AI",
          children: [
            { path: "/agent-workflows", title: "Agent 流程", icon: "AI", permission: "agent:workflow" }
          ]
        },
        {
          key: "office",
          title: "内部办公",
          icon: "办",
          children: [
            { path: "/design-deliveries", title: "设计交付", icon: "稿", permission: "office:design:view" },
            { path: "/office-rectification", title: "整改事项", icon: "改", permission: "office:rectification:view" },
            { path: "/machine-learning", title: "机器学习", icon: "ML", permission: "office:ml:view" }
          ]
        },
        {
          key: "system",
          title: "账号与权限",
          icon: "权",
          children: [
            { path: "/admin-accounts", title: "内部账号", icon: "账", permission: "system:account:view" },
            { path: "/admin-roles", title: "角色权限", icon: "角", permission: "system:role:view" }
          ]
        }
      ]
    }
  },
  computed: {
    currentUser() {
      return this.currentUserData || {}
    },
    currentPermissions() {
      const user = this.currentUserData || {}
      if (user.superAdmin) {
        return null
      }
      return Array.isArray(user.permissions) ? user.permissions : []
    },
    visibleMenuGroups() {
      return this.menuGroups
        .map(group => ({ ...group, children: group.children.filter(item => this.canSeeMenu(item.permission)) }))
        .filter(group => group.children.length)
    },
    currentTitle() {
      return this.$route.meta.title || "工作台"
    },
    currentSubtitle() {
      const subtitles = {
        aiImages: "后台持续执行生图任务，并统一管理生成图片与交付引用。",
        bannerSettings: "维护小程序首页轮播图，并为每张图设置点击后跳转的页面。",
        squareTopics: "维护广场发帖话题，同步到发帖页、广场筛选与帖子详情。",
        about: "维护小程序关于我们页面展示的品牌与团队信息。",
        designDeliveries: "按文件夹管理界面图、关联图标与开发状态。",
        officeRectification: "将整改问题整理为可跟踪的完成事项。",
        machineLearning: "查看内部算法目录、流程说明与蛇类体重趋势预测。",
        merchantAccounts: "为已入驻店铺创建独立的商户登录账号。",
        activityLottery: "审核小程序提交的抽奖报名，核对头像昵称、参赛帖与收货信息。",
        activityConfig: "维护抽奖活动的信息、时间、文案与奖项，可复用开新活动。",
        adminAccounts: "管理内部成员的登录账号与角色。",
        adminRoles: "按工作职责分配菜单和操作权限。"
      }
      return subtitles[this.$route.name] || "统一管理用户、宠物、运营配置和内部协作。"
    }
  },
  watch: {
    "$route.path": {
      handler() {
        this.mobileMenuOpen = false
        this.expandActiveGroup()
      },
      immediate: true
    }
  },
  methods: {
    canSeeMenu(permissionCode) {
      if (!permissionCode) {
        return true
      }
      const permissions = this.currentPermissions
      if (permissions === null) {
        return true
      }
      return permissions.indexOf(permissionCode) >= 0
    },
    async refreshAdminPermissions() {
      try {
        const res = await fetchCurrentAdmin()
        if (res && res.data) {
          updateAdminUser(res.data)
          // 权限可能被角色调整过，这里同步到本地快照，菜单显隐会立即重新计算。
          this.currentUserData = res.data
        }
      } catch (error) {
        // 会话失效时会由请求拦截器统一跳转登录页，这里忽略即可。
      }
    },
    isGroupOpen(groupKey) {
      return this.openMenuKeys[groupKey] !== false
    },
    isGroupActive(group) {
      return group.children.some(item => item.path === this.$route.path)
    },
    toggleMenuGroup(groupKey) {
      this.$set(this.openMenuKeys, groupKey, !this.isGroupOpen(groupKey))
    },
    expandActiveGroup() {
      const activeGroup = this.visibleMenuGroups.find(group => this.isGroupActive(group))
      if (activeGroup) {
        this.$set(this.openMenuKeys, activeGroup.key, true)
      }
    },
    async logout() {
      try {
        await logoutAdmin()
      } catch (error) {
        // Local session still needs to be cleared when the server session has expired.
      } finally {
        clearAdminSession()
        this.$router.push("/login")
      }
    }
  }
}
</script>

<style scoped>
.layout {
  height: 100vh;
  display: flex;
  background: #f5f7fa;
  overflow: hidden;
}

.layout-sidebar {
  width: 248px;
  padding: 24px 18px;
  background: #ffffff;
  border-right: 1px solid #e8edf3;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
}

.brand-card {
  display: flex;
  align-items: center;
  padding: 14px;
  border-radius: 20px;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
  border: 1px solid #e8edf3;
}

.brand-mark {
  width: 44px;
  height: 44px;
  line-height: 44px;
  text-align: center;
  border-radius: 14px;
  background: #2563eb;
  color: #ffffff;
  font-weight: 700;
  margin-right: 12px;
}

.brand-title {
  color: #111827;
  font-size: 18px;
  font-weight: 700;
}

.brand-desc {
  margin-top: 4px;
  color: #94a3b8;
  font-size: 12px;
}

.menu-list {
  display: flex;
  flex-direction: column;
  margin-top: 18px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
}

.menu-group {
  margin-bottom: 8px;
}

.menu-group__head {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
}

.menu-group__head:hover,
.menu-group__head--active {
  background: #f8fafc;
  color: #111827;
}

.menu-group__icon {
  width: 26px;
  height: 26px;
  line-height: 26px;
  text-align: center;
  border-radius: 9px;
  background: #eef2f7;
  margin-right: 10px;
  font-size: 11px;
  font-weight: 800;
}

.menu-group__title {
  flex: 1;
  min-width: 0;
  text-align: left;
  font-size: 13px;
  font-weight: 700;
}

.menu-group__arrow {
  color: #94a3b8;
  font-size: 16px;
  transform: rotate(-90deg);
  transition: transform 0.18s ease;
}

.menu-group__arrow--open {
  transform: rotate(0deg);
}

.submenu-list {
  margin: 4px 0 4px 13px;
  padding-left: 12px;
  border-left: 1px solid #e8edf3;
}

.menu-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 4px;
  border-radius: 12px;
  color: #475569;
  text-decoration: none;
  font-size: 14px;
}

.menu-item:hover {
  background: #f8fafc;
}

.menu-item--active {
  background: #eef4ff;
  color: #2563eb;
  font-weight: 600;
}

.menu-item__icon {
  width: 26px;
  height: 26px;
  line-height: 26px;
  text-align: center;
  border-radius: 10px;
  background: #f8fafc;
  margin-right: 10px;
  font-size: 11px;
  font-weight: 700;
}

.layout-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.layout-header {
  position: sticky;
  top: 0;
  z-index: 20;
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 64px;
  padding: 10px 28px;
  background: rgba(255, 255, 255, 0.96);
  border-bottom: 1px solid #e8edf3;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.04);
  backdrop-filter: blur(10px);
}

.layout-header__copy {
  min-width: 0;
}

.layout-title {
  color: #111827;
  font-size: 20px;
  line-height: 26px;
  font-weight: 800;
}

.layout-subtitle {
  margin-top: 2px;
  color: #6b7280;
  font-size: 12px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.layout-user {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 18px;
}

.layout-user__badge {
  margin-right: 10px;
  padding: 6px 10px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e8edf3;
  color: #475569;
  font-size: 12px;
}

.ghost-button {
  height: 36px;
  padding: 0 14px;
  border: 1px solid #d7deea;
  border-radius: 12px;
  background: #ffffff;
  color: #334155;
  cursor: pointer;
}

.layout-content {
  flex: 1;
  min-height: 0;
  padding: 12px 28px 24px;
  overflow-x: hidden;
  overflow-y: auto;
}

.mobile-menu-button,
.mobile-menu-overlay {
  display: none;
}

@media (max-width: 760px) {
  .layout-sidebar {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 70;
    width: min(280px, 82vw);
    padding: 16px 14px;
    transform: translateX(-102%);
    transition: transform 0.2s ease;
    box-shadow: 14px 0 34px rgba(15, 23, 42, 0.16);
  }

  .layout-sidebar--open {
    transform: translateX(0);
  }

  .mobile-menu-overlay {
    position: fixed;
    inset: 0;
    z-index: 65;
    display: block;
    border: 0;
    background: rgba(17, 24, 39, 0.34);
  }

  .mobile-menu-button {
    width: 36px;
    height: 36px;
    display: grid;
    place-content: center;
    gap: 4px;
    flex: 0 0 auto;
    margin-right: 10px;
    border: 1px solid #d7deea;
    border-radius: 6px;
    background: #ffffff;
  }

  .mobile-menu-button span {
    width: 15px;
    height: 2px;
    background: #526171;
  }

  .layout-header {
    padding: 9px 12px;
  }

  .layout-header__copy {
    flex: 1;
  }

  .layout-title {
    font-size: 17px;
  }

  .layout-subtitle {
    max-width: 46vw;
  }

  .layout-user__badge {
    display: none;
  }

  .layout-user .ghost-button {
    height: 34px;
    padding: 0 10px;
  }

  .layout-content {
    padding: 8px 12px 18px;
  }
}
</style>
